import { JSX, MouseEventHandler, useMemo } from "react"
import { DeepReadonly } from "ts-essentials"
import { IconButton, IconButtonOwnProps, ListItem, ListItemText } from "@mui/material"
import { Clear } from "@mui/icons-material"
import moment from "moment"
import { DATE_FORMAT_LONG } from "helpers/dates"
import DynamicEllipsisWithTooltip from "components/dynamic-ellipsis-with-tooltip"
import { InstrumentMetadata } from "helpers/types/instruments/metadata"
import { IsoDate } from "helpers/types/dates"

// Makes Storybook look better
import "../selected-position/styles.scss"

const DEFAULT_ICON = <Clear fontSize="small" />

export default function SelectedPosition({ labelPrefix, instrumentMetadata, onClick, onIconClick, icon = DEFAULT_ICON, iconColor = "secondary", size = "large", iconClassName, startDate, endDate }: DeepReadonly<SelectedPositionProps>) {
  // [TODO] Should this be a function on the instrument context to decide how to render it?
  const [itemText, title] = useMemo(() => {
    const positionName = instrumentMetadata.name || "Unknown Instrument"
    if (startDate && endDate) {
      const endVsStart = [startDate, endDate]
        .map(date => moment(date).format(DATE_FORMAT_LONG))
        .join(" vs ")

      return [
        "End vs start date",
        // In a tooltip, an array of strings shows up on multiple lines
        [positionName, endVsStart],
      ]
    }
    return [positionName, positionName]
  }, [startDate, endDate, instrumentMetadata])

  let additionalProps = {}
  if (onClick) {
    additionalProps = {
      component: "button",
      onClick,
      className: "selected-position-text-button",
      "data-testid": "selected-position-text-button",
    }
  }

  return <ListItem component="div" className="selected-position">
    <ListItemText
      primary={<DynamicEllipsisWithTooltip
        title={title}
      >
        <>
          {labelPrefix}
          {itemText}
        </>
      </DynamicEllipsisWithTooltip>}
      {...additionalProps}
    />
    <IconButton color={iconColor} onClick={onIconClick} size={size} data-testid="selected-position-icon-button" className={iconClassName}>
      {icon}
    </IconButton>
  </ListItem>
}

interface SelectedPositionProps {
  instrumentMetadata: InstrumentMetadata
  icon?: JSX.Element
  iconColor?: IconButtonOwnProps["color"]
  iconClassName?: string
  onIconClick: MouseEventHandler<HTMLButtonElement>
  onClick?: MouseEventHandler<HTMLButtonElement>
  size?: IconButtonOwnProps["size"]
  labelPrefix?: string
  startDate?: IsoDate
  endDate?: IsoDate
}
